import type {
  QgammonAbandonedReason,
  QgammonEndReason,
  QgammonMatchSummary,
} from '@/types/qgammon';

/**
 * How a finished QGammon match ended, resolved for display.
 *
 * Two independent server columns answer this — `endReason` on `completed`
 * matches, `abandonedReason` on `abandoned` ones — and BOTH are additive and
 * nullable: they arrive as `undefined` from a qgammon-api build predating them
 * and as `null` on rows written before the `20260818000000_add_end_reason`
 * migration. So the resolution is deliberately three-tiered:
 *
 *   1. the column, when present AND a value we know how to label;
 *   2. otherwise the STATUS heuristic — `abandoned` alone still tells the user
 *      the match was interrupted, which is the part they care about;
 *   3. otherwise nothing. A `completed` match with no reason renders no badge,
 *      exactly as it does today: the win/loss marker already carries the
 *      outcome, and inventing "played out" for a row we know nothing about
 *      would be a claim, not a fallback.
 *
 * Unknown FUTURE values fall to tier 2/3 rather than being shown raw — both
 * columns are strings on purpose server-side, so new reasons WILL appear and
 * must degrade instead of leaking an identifier into the UI.
 *
 * Tier 2 is NOT dead weight now that the migration has landed in production
 * (2026-08-30 ~08:53Z). Rows written BEFORE it keep `endReason` NULL forever —
 * the column was backfilled with nothing, because the information never
 * existed. So from today this resolver is the only thing holding the old
 * history and the new one in one list: without tier 2 every pre-migration
 * match would silently lose the one fact the user cares about.
 */
export type QgammonEndDescriptor =
  | { source: 'endReason'; key: QgammonEndReason }
  | { source: 'abandonedReason'; key: QgammonAbandonedReason }
  | { source: 'status'; key: 'abandoned' }
  | null;

const KNOWN_END_REASONS: readonly QgammonEndReason[] = [
  'normal',
  'resign',
  'timeout',
  'graceForfeit',
  // ADR-0171 §2.7 (26/09): le partite di torneo «a tavolino». Senza queste due chiavi cadevano sul
  // livello «motivo ignoto» — il vincitore giusto, ma nessuna parola su COME: proprio la cosa che
  // chi non si e' seduto deve leggere.
  'noShow',
  'noShowLot',
];

const KNOWN_ABANDONED_REASONS: readonly QgammonAbandonedReason[] = [
  'server_restart',
  'never_started',
  // Aggiunti il 07/09 dopo averli letti alla fonte: il server ne dichiara CINQUE
  // (`persistence.ts:397-403`) e qui ne conoscevamo due, quindi `expired` e
  // `cancelled` — cioe' i due esiti di un APPUNTAMENTO — cadevano al livello 2
  // («interrotta»), che e' vero ma tace proprio sulla differenza che l'utente
  // vuole: nessuno si e' presentato, oppure l'altro ha disdetto.
  'never_played',
  'expired',
  'cancelled',
];

function isEndReason(v: string): v is QgammonEndReason {
  return (KNOWN_END_REASONS as readonly string[]).includes(v);
}

function isAbandonedReason(v: string): v is QgammonAbandonedReason {
  return (KNOWN_ABANDONED_REASONS as readonly string[]).includes(v);
}

/** Resolve why a match ended. Returns null when nothing truthful can be said. */
export function matchEndDescriptor(m: QgammonMatchSummary): QgammonEndDescriptor {
  if (m.status === 'abandoned') {
    const reason = m.abandonedReason;
    if (reason && isAbandonedReason(reason)) return { source: 'abandonedReason', key: reason };
    return { source: 'status', key: 'abandoned' };
  }
  if (m.status === 'completed') {
    const reason = m.endReason;
    if (reason && isEndReason(reason)) return { source: 'endReason', key: reason };
  }
  return null;
}

/**
 * i18n key (namespace `backgammon`) for a descriptor. Kept next to the
 * resolver so a new reason cannot be added in one place and forgotten in the
 * other — `endReason.<key>` must exist in BOTH `en` and `it`.
 */
export function endDescriptorKey(d: NonNullable<QgammonEndDescriptor>): string {
  return `endReason.${d.key}`;
}

/**
 * Was this match played against the bot? Reads the TOP-LEVEL `whiteIsBot` /
 * `blackIsBot` — the player objects carry no `isBot`, which is what the old
 * type wrongly claimed. Absent flags (older API build) read as "not a bot",
 * i.e. no badge, which is the safe direction: it under-labels rather than
 * calling a human match practice.
 */
export function isBotMatch(m: QgammonMatchSummary): boolean {
  return m.whiteIsBot === true || m.blackIsBot === true;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * L'ESITO DI FINE MATCH — dal payload `match:ended` del SOCKET
 * [Luca, 2026-09-14: «Quando si vince o si perde, indicare risultato finale e
 *  se squalificati indicare ragione di vittoria/sconfitta.»]
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ═══ PERCHE' QUI DENTRO E NON IN UN FILE ACCANTO ═══
 * Le due meta' di questo file rispondono alla stessa domanda — *perche' e'
 * finita* — su due FONTI diverse: sopra la riga REST (`GET /matches/mine`,
 * `QgammonMatchSummary`), qui il payload del socket (`match:ended`). Nomi dei
 * campi diversi, ma **lo stesso vocabolario di ragioni e le stesse chiavi
 * `endReason.*`**.
 * ⇒ Separarli in due moduli avrebbe prodotto due elenchi di ragioni conosciute
 * che divergono al primo valore nuovo, in silenzio e con tutti i cancelli
 * verdi: e' la classe di guasto che questo repo ha gia' pagato (la seconda
 * copia della revalidation dei ruoli, il secondo parser di date mai cablato).
 * Qui `isAbandonedReason`, `isEndReason` e `endDescriptorKey` sono **gli
 * stessi**: aggiungere una ragione in cima la insegna a tutte e due le fonti.
 *
 * ═══ 🔴 I NOMI DEI CAMPI SONO LETTI, NON DEDOTTI ═══
 * [M 2026-09-14] `MatchEndedPayloadSchema` in
 * `node_modules/@qlashgeminiz/qgammon-board-ui/node_modules/@qlashgeminiz/qgammon-types/dist/events.d.ts:1159-1270`
 * — cioe' `qgammon-types@0.11.0`, la copia **annidata sotto `qgammon-board-ui`**,
 * che e' quella che tipa `useBackgammonSocket().ended`.
 *   `winner: "W" | "B"`                  OBBLIGATORIO — e SEGNAPOSTO nel ramo senza risultato
 *   `outcome?: "result" | "no_result"`   il discriminante di ADR-0119
 *   `abandonedReason?: string`           presente SOLO se la partita e' chiusa `abandoned`
 *   `endReason?: string`                 normal · resign · timeout · graceForfeit · cancelled
 *                                        · noShow · noShowLot (ADR-0171, qgammon-types 0.12.0)
 *   `finalScore?: { W: number; B: number }`
 *   `cancelledBy?` · `endedAt?` · `matchId` · `magnitude` · `cubeValue` · `points`
 *   · `seedRevealed` · `matchLength?`
 *
 * ⚠️ **ATTENZIONE alla versione doppia, ed e' misurata**: il `@qlashgeminiz/qgammon-types`
 * di PRIMO livello e' ancora `0.2.0` e **non** dichiara `outcome`, `abandonedReason`,
 * `cancelledBy`, `endedAt`, ne' `endReason: 'cancelled'`. Chi importa i tipi da li' non
 * vede meta' di questo contratto. La copia annidata si', e la app riceve quella.
 * 📌 ⇒ **La riga di `BackgammonMatch.tsx:181-182` che dice «`outcome` non e' nei tipi che
 * questa app riceve» e' SCADUTA**: verificato il 14/09 con una sonda `tsc` (cinque letture
 * dei campi verdi, piu' un controllo negativo su un campo inventato, rosso — senza il
 * controllo negativo la sonda non avrebbe provato niente).
 * 📌 ANNOTAZIONE 29/09 (aggiornamento a `qgammon-types@0.16.0` / `board-ui@0.11.0`): la «versione
 * doppia» non c'e' piu'. Il primo livello e' 0.16.0 e la copia annidata sotto `board-ui` e' sparita
 * (deduplicata: `board-ui@0.11.0` dipende da types `0.16.0`). Quanto sopra e' la misura del 14/09.
 *
 * ═══ ⛔ COSA QUESTO MODULO NON SA DIRE, E NON LO INVENTA ═══
 * · **«SQUALIFICATO» NON ESISTE SU QUESTO FILO.** Le ragioni che il gameserver puo'
 *   mandare sono undici — `MATCH_END_REASONS` (normal, resign, timeout, graceForfeit,
 *   cancelled, e dal 26/09 noShow e noShowLot di ADR-0171) e `MATCH_ABANDONED_REASONS`
 *   (never_played, never_started, expired, cancelled) — e **nessuna** e' una squalifica
 *   (ne' i due «a tavolino»: chi non si e' presentato non e' stato squalificato da nessuno). Mappare `graceForfeit` o
 *   `timeout` su «squalificato» sarebbe coniare un fatto: chi si e' disconnesso non e'
 *   stato squalificato da nessuno. La parola esiste altrove nella piattaforma
 *   (`ritiro.squalificato` nei tornei Limulo), ed e' un'ALTRA cosa.
 * · **`cancelledBy` NON e' esposto di proposito.** E' un'identita' (chi ha disdetto), e
 *   qui l'unica cosa che sappiamo del giocatore locale e' la sua `perspective`, cioe' un
 *   POSTO al tavolo ('W'/'B'), non un'identita'. Esporlo inviterebbe il confronto che non
 *   si puo' fare, e il giorno che qualcuno lo scrivesse direbbe «l'hai disdetta tu» a chi
 *   non l'ha disdetta. Torna sul filo: si aggiunge quando arrivera' anche la chiave per
 *   confrontarlo.
 */

/** Chi ha vinto, **dal punto di vista del giocatore locale**. */
export type QgammonEndVerdict =
  /** Ho vinto io. */
  | 'won'
  /** Ha vinto l'altro. */
  | 'lost'
  /** **Non ha vinto nessuno** — e va detto, non taciuto. */
  | 'none'
  /** Il payload c'e' ma non dice chi: ⛔ non si annuncia niente. */
  | 'unknown';

/**
 * La CLASSE del motivo. Sei (dal 26/09 anche `no-show`, ADR-0171), e `unknown` **e' un esito
 * legittimo**: se il server non dice perche', questo modulo non lo inventa.
 */
export type QgammonEndMotive =
  /** Giocata fino in fondo (`endReason: 'normal'`). */
  | 'played-out'
  /** Qualcuno ha smesso: ritiro, tempo scaduto, forfait da disconnessione. */
  | 'forfeit'
  /** Chiusa come `abandoned`: appuntamento scaduto/disdetto, mai giocata. Nessun vincitore. */
  | 'abandoned'
  /** `outcome: 'no_result'` senza una ragione: nessun vincitore, motivo ignoto. */
  | 'no-result'
  /**
   * «A tavolino» (ADR-0171): `noShow` (vince chi si e' seduto) o `noShowLot` (non si e' seduto
   * nessuno: sorteggio). Il vincitore c'e' ed e' vero, ma una partita fra due giocatori no.
   */
  | 'no-show'
  /** Il server non ha detto perche'. */
  | 'unknown';

/**
 * CHE COSA E' il punteggio che si sta per mostrare — ed e' il punto di Luca
 * «indicare risultato finale».
 *
 * 🔴 Il difetto da cui nasce e' di **ETICHETTA, non di numero**: `Resign match`
 * «termina match indipendentemente dai punti» (specifica di Luca del 12/09), e
 * chiamare «punteggio finale» il tabellino di un match che a punti non e' stato
 * deciso afferma una cosa che non e' successa. Stessa famiglia del «Hai vinto!»
 * su una partita mai giocata, un gradino piu' in basso.
 */
export type QgammonEndScoreKind =
  /** Il match e' stato giocato fino in fondo: il punteggio **e'** il risultato. */
  | 'final'
  /**
   * Il match e' finito in un altro modo (o non sappiamo quale): il punteggio e'
   * **dov'era quando e' finito**, e l'etichetta non promette altro.
   */
  | 'atEnd'
  /** Non c'e' niente da mostrare — o e' un RITIRO, dove il punteggio non si mostra (v. §4). */
  | 'none';

/** L'esito di fine match, gia' risolto per lo schermo. */
export interface QgammonMatchEnd {
  verdict: QgammonEndVerdict;
  motive: QgammonEndMotive;
  /**
   * Chiave i18n della frase-TITOLO. ⚠️ Le chiavi di vittoria/sconfitta sono
   * quelle che la schermata usa **gia'** (`match.youWon` / `match.youLost`): non
   * se ne coniano di gemelle, o le due copie divergono alla prima revisione e la
   * stessa app dice due cose diverse dello stesso fatto.
   */
  verdictKey: string;
  /**
   * Chiave i18n del motivo PRECISO (`endReason.*`), per una riga sotto il titolo —
   * oppure `null`.
   * ⭐ `null` significa due cose e nessuna e' un ripiego: **il server non ha detto
   * perche'** (motivo `unknown`), oppure **il motivo E' GIA' il titolo** e ripeterlo
   * sarebbe stampare due volte la stessa frase. Garantito: `reasonKey !== verdictKey`.
   */
  reasonKey: string | null;
  /**
   * Il punteggio dal punto di vista locale, o `null`.
   * ⛔ `null` **anche quando il payload un `finalScore` ce l'ha**, se non ha vinto
   * nessuno: su una riga senza vincitore il punteggio non e' un risultato, ed e'
   * lo stesso annuncio di prima scritto in cifre. E `null` su un RITIRO (v. §4).
   */
  score: { mine: number; theirs: number } | null;
  scoreKind: QgammonEndScoreKind;
  /** Chiave i18n dell'etichetta del punteggio, o `null` quando punteggio non ce n'e'. */
  scoreLabelKey: string | null;
}

/** Il campo, se e' una stringa non vuota. Vale per `abandonedReason` e `endReason`. */
function stringField(o: object, name: string): string | null {
  const raw = (o as Record<string, unknown>)[name];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * L'ESITO DI FINE MATCH dal payload `match:ended`.
 *
 * `ended` si prende `unknown` di proposito — la stessa scelta di
 * `statusOfMatchState`: il chiamante ha in mano un tipo che dipende da QUALE
 * copia di `qgammon-types` gli risolve, e un parametro stretto costringerebbe
 * lui al cast, cioe' sposterebbe fuori la cosa che questo file esiste per
 * tenere dentro.
 *
 * `perspective` e' il POSTO del giocatore locale ('W'/'B'), quello che la
 * schermata gia' ha — oppure `null`, «NON SI SA» (`lib/qgammon/prospettiva.ts`, I-195).
 * ⛔ Con `null` non si dice chi ha vinto «per me» e non si mostra un punteggio mio–tuo: il
 * verdetto e' `unknown` («Partita finita») e `score` e' `null`. Un ripiego su `'W'` qui dentro
 * era esattamente il difetto: il Nero leggeva il verdetto del Bianco.
 *
 * ⇒ `null` quando `ended` non c'e': **niente da dire, e non lo si dice.**
 */
export function matchEndOutcome(
  ended: unknown,
  perspective: 'W' | 'B' | null,
): QgammonMatchEnd | null {
  if (!ended || typeof ended !== 'object') return null;

  const abandoned = stringField(ended, 'abandonedReason');
  const endReason = stringField(ended, 'endReason');
  const outcome = (ended as { outcome?: unknown }).outcome;

  // ── 1. NESSUN VINCITORE ───────────────────────────────────────────────────
  // 🔴 L'ordine non e' arbitrario: questo ramo viene PRIMA di leggere `winner`,
  // perche' `winner` e' obbligatorio nello schema e il server ce lo mette anche
  // quando un vincitore non c'e' (segnaposto strutturale, `reaper.ts:118-131`).
  // ⛔ **Qualunque valore presente e' abbastanza per NON pronunciare un
  // vincitore**, anche uno che non sappiamo tradurre: quel campo esiste SOLO
  // sulle righe senza vincitore. Sapere COME dirlo e' un problema di copy;
  // sapere di non dover dire «hai vinto» no.
  //
  // ⚠️ E se arrivasse `outcome: 'result'` INSIEME a un `abandonedReason` — una
  // contraddizione che il contratto non vieta — vince l'abbandono: fra due
  // letture in disaccordo si prende quella che afferma di meno.
  //
  // `endReason: 'cancelled'` entra qui e non fra i forfait: sul filo «cancelled»
  // e' un APPUNTAMENTO disdetto (v. il commento di `MATCH_ABANDONED_REASONS`),
  // cioe' un non-risultato, e lo si emette su entrambi i campi. Da solo, senza
  // `abandonedReason`, dire «hai vinto» sarebbe il difetto di ADR-0119 rifatto
  // da un campo diverso.
  const noWinner = abandoned ?? (endReason === 'cancelled' ? 'cancelled' : null);
  if (noWinner !== null) {
    // Livello 1: la ragione, quando la sappiamo etichettare. Livello 2: «Interrotta»,
    // che e' vero anche per un valore che il server aggiungera' domani.
    const key = isAbandonedReason(noWinner)
      ? endDescriptorKey({ source: 'abandonedReason', key: noWinner })
      : endDescriptorKey({ source: 'status', key: 'abandoned' });
    return {
      verdict: 'none',
      motive: 'abandoned',
      verdictKey: key,
      // Il motivo E' il titolo: ripeterlo sotto stamperebbe due volte la stessa frase.
      reasonKey: null,
      score: null,
      scoreKind: 'none',
      scoreLabelKey: null,
    };
  }

  // `outcome: 'no_result'` SENZA una ragione. [M 2026-09-09, emettitore per emettitore]
  // oggi non capita: i tre punti che dichiarano `no_result` portano tutti un
  // `abandonedReason`. Il ramo c'e' lo stesso perche' `outcome` **e' il
  // discriminante dichiarato da ADR-0119** — e il giorno che un emettitore lo
  // mandi da solo, il ramo sopra non lo vedrebbe e questa funzione annuncerebbe
  // un vincitore leggendo un segnaposto. ⛔ Costa una riga: non e' il posto dove
  // risparmiarla.
  if (outcome === 'no_result') {
    return {
      verdict: 'none',
      motive: 'no-result',
      verdictKey: 'match.esito.nessunRisultato',
      reasonKey: null,
      score: null,
      scoreKind: 'none',
      scoreLabelKey: null,
    };
  }

  // ── 2. C'E' UN RISULTATO: chi ha vinto, dal mio punto di vista ────────────
  const winner = (ended as { winner?: unknown }).winner;
  const verdict: QgammonEndVerdict =
    (winner === 'W' || winner === 'B') && perspective !== null
      ? winner === perspective
        ? 'won'
        : 'lost'
      : 'unknown';

  // ── 3. IL MOTIVO ──────────────────────────────────────────────────────────
  // ⚠️ Un `endReason` che non sappiamo etichettare NON toglie il vincitore: cio'
  // che dice «non ha vinto nessuno» e' il ramo 1, non questo. Qui si degrada a
  // `unknown` — motivo ignoto, esito noto — e `reasonKey` resta `null` invece di
  // far uscire un identificatore grezzo sullo schermo.
  const known = endReason !== null && isEndReason(endReason);
  const tavolino = endReason === 'noShow' || endReason === 'noShowLot';
  const motive: QgammonEndMotive = !known
    ? 'unknown'
    : endReason === 'normal'
      ? 'played-out'
      : tavolino
        ? 'no-show'
        : 'forfeit';

  // 🔴 ADR-0171 §2.7 — COSA VEDE IL GIOCATORE, testi dell'ADR:
  //  · `noShow` → al vincitore «Vittoria a tavolino: l'avversario non si è presentato», all'assente
  //    «Sconfitta a tavolino: non ti sei presentato». La frase porta GIA' il verdetto: e' il titolo,
  //    e sotto non si ripete niente (`reasonKey` null, come per gli abbandoni);
  //  · `noShowLot` → «Nessuno si è presentato: esito per sorteggio», col verdetto DAL MIO COLORE
  //    (TMA #397): titolo «Hai vinto!»/«Hai perso», la frase sotto come ragione.
  // Col colore ignoto (`perspective` null) non si dice «hai vinto» ne' «non ti sei presentato» a
  // nessuno: il titolo e' «Partita finita». Sotto: per `noShow` la ragione BREVE dello storico («A
  // tavolino»); per `noShowLot` la frase dell'ADR, che di me non dice niente e quindi vale per tutti.
  // (Revisione di #452, punto 5: la prima stesura diceva «ragione breve» per entrambi.)
  let reasonKey =
    known && endReason !== null ? endDescriptorKey({ source: 'endReason', key: endReason }) : null;
  let tavolinoKey: string | null = null;
  if (endReason === 'noShow' && verdict !== 'unknown') {
    tavolinoKey = verdict === 'won' ? 'match.esito.noShowWon' : 'match.esito.noShowLost';
    reasonKey = null;
  } else if (endReason === 'noShowLot') {
    reasonKey = 'match.esito.noShowLot';
  }

  // ── 4. IL PUNTEGGIO, E COME SI CHIAMA ─────────────────────────────────────
  // «Punteggio finale» si dice SOLO di un match giocato fino in fondo.
  // 🔴 Su un RITIRO il punteggio NON SI MOSTRA affatto [Luca, L-2265 (b), 2026-09-26: «su un
  // ritiro niente riga del punteggio: resta "Hai vinto! · Ritiro"»]. Il ritiro «termina match
  // indipendentemente dai punti», e il tabellino di dov'era non dice niente di come e' finito:
  // restano il verdetto e la ragione. ⇒ `scoreKind: 'none'` e `score: null`.
  // Sugli ALTRI motivi non giocati fino in fondo (tempo scaduto, forfait da disconnessione) e su
  // un motivo che non conosciamo il punteggio resta, ed e' dov'era quando e' finito: l'etichetta
  // dice quello e basta («Punteggio alla fine»). ⚠️ Luca ha deciso sul ritiro e SOLO su quello: gli
  // altri non si allineano per analogia. *Fra due etichette, quella che promette meno.*
  // 🔴 E dal 26/09 anche sulle partite «A TAVOLINO» (`noShow`, `noShowLot`) il punteggio NON SI MOSTRA
  // [Luca, I-222, ~23:51Z, scelta (a) sulla domanda «0–0 a tavolino»]: una partita che nessuno ha
  // giocato non ha un tabellino, e lo «0–0» che il server lascia (`finalScore = match.score`) non dice
  // niente di come e' finita. Resta il titolo. ⚠️ E' una SECONDA decisione, non un'analogia col ritiro:
  // tempo scaduto e forfait da disconnessione continuano a mostrare «Punteggio alla fine».
  const fs = (ended as { finalScore?: unknown }).finalScore;
  const hasScore =
    !!fs &&
    typeof fs === 'object' &&
    typeof (fs as { W?: unknown }).W === 'number' &&
    typeof (fs as { B?: unknown }).B === 'number';
  const w = hasScore ? (fs as { W: number }).W : 0;
  const b = hasScore ? (fs as { B: number }).B : 0;
  const ritiro = endReason === 'resign';
  const senzaPunteggio = ritiro || tavolino;
  const scoreKind: QgammonEndScoreKind = !hasScore || senzaPunteggio
    ? 'none'
    : motive === 'played-out'
      ? 'final'
      : 'atEnd';

  return {
    verdict,
    motive,
    verdictKey:
      tavolinoKey ??
      (verdict === 'won'
        ? 'match.youWon'
        : verdict === 'lost'
          ? 'match.youLost'
          : 'match.esito.finita'),
    reasonKey,
    score:
      hasScore && !senzaPunteggio && perspective !== null
        ? { mine: perspective === 'W' ? w : b, theirs: perspective === 'W' ? b : w }
        : null,
    scoreKind,
    scoreLabelKey:
      scoreKind === 'final'
        ? 'match.esito.punteggioFinale'
        : scoreKind === 'atEnd'
          ? 'match.esito.punteggioAllaFine'
          : null,
  };
}
